import * as MutationTypes from "../mutation_types"
import { State, Corporation, AuthHeaders, MailRecipient, User } from "../store_types"
import { callCorporationsInChargeAPI, callCorporationAPI, callCorporationMonthlyMailRecipientsAPI, callCorporationsNestedTreeAPI } from "../../api/corporations"
import { AxiosResponseHeaders } from "axios"

const getDefaultState  = () => {
  return {
    corporation: null,
    corporationsInCharge : [],
    monthlyMailRecipients: [],
    corporationsNestedTree: [],
    normalCorporationsCount: 0
  }
}

const corporations = {
  state: () => getDefaultState(),

  getters: {
    corporation (state : State) {
      return state.corporation
    },
    corporationsInCharge (state : State) {
      return state.corporationsInCharge
    },
    monthlyMailRecipients (state : State) {
      return state.monthlyMailRecipients
    },
    corporationsNestedTree (state : State) {
      return state.corporationsNestedTree
    },
    normalCorporationsCount (state : State) {
      return state.normalCorporationsCount
    },
  },

  actions: {
    fetchCorporation({ commit, getters }: { commit: (mutationType:string, resultData: AxiosResponseHeaders | Corporation) => void, getters: { [key: string]: AuthHeaders | number }}){
      return new Promise<void>((resolve, reject) => {
        callCorporationAPI(
          getters.authHeaders as AuthHeaders,
          getters.selectedCorporationId as number,
          // Success
          (headers: AxiosResponseHeaders, corporation: Corporation) => {
            commit(MutationTypes.ASSIGN_AUTH_HEADERS, headers)
            commit(MutationTypes.ASSIGN_CORPORATION, corporation)
            resolve()
          },
          // Failure
          () => {
            reject()
          })
      })
    },

    fetchCorporationsInCharge({ commit, getters }: { commit: (mutationType:string, resultData: AxiosResponseHeaders | Array<Corporation>) => void, getters: { [key: string]: AuthHeaders }}){
      return new Promise<void>((resolve, reject) => {
        callCorporationsInChargeAPI(
          getters.authHeaders,
          // Success
          (headers: AxiosResponseHeaders, corporationsInCharge: Array<Corporation>) => {
            commit(MutationTypes.ASSIGN_AUTH_HEADERS, headers)
            commit(MutationTypes.ASSIGN_CORPORATIONS_IN_CHARGE, corporationsInCharge)
            resolve()
          },
          // Failure
          () => {
            reject()
          })
      })
    },

    fetchMonthlyMailRecipients({ commit, getters }: { commit: (mutationType:string, resultData: AxiosResponseHeaders | Array<MailRecipient>) => void, getters: { [key: string]: AuthHeaders | number | User }}){
      const currentUser = getters.currentUser as User
      if( currentUser.type != "Sales") return

      return new Promise<void>((resolve, reject) => {
        callCorporationMonthlyMailRecipientsAPI(
          getters.authHeaders as AuthHeaders,
          getters.selectedCorporationId as number,
          // Success
          (headers: AxiosResponseHeaders, monthlyMailRecipients: Array<MailRecipient>) => {
            commit(MutationTypes.ASSIGN_AUTH_HEADERS, headers)
            commit(MutationTypes.ASSIGN_MONTHLY_MAIL_RECIPIENTS, monthlyMailRecipients)
            resolve()
          },
          // Failure
          () => {
            reject()
          })
      })
    },

    fetchCorporationsNestedTree({ dispatch, commit, getters }: { dispatch: (actionName: string, corporation?: Corporation) => void, commit: (mutationType:string, resultData: AxiosResponseHeaders | Array<Corporation>) => void, getters: { [key: string]: AuthHeaders }}){
      return new Promise<void>((resolve, reject) => {
        callCorporationsNestedTreeAPI(
          getters.authHeaders,
          // Success
          (headers: AxiosResponseHeaders, corporationsNestedTree: Array<Corporation>) => {
            commit(MutationTypes.ASSIGN_AUTH_HEADERS, headers)
            commit(MutationTypes.ASSIGN_CORPORATIONS_NESTED_TREE, corporationsNestedTree)
            // 総企業数を取得しておく
            dispatch("resetNormalCorporationsCount")
            for (const corporation of corporationsNestedTree as Array<Corporation>) {
              dispatch("incrementNormalCorporationsCount", corporation)
            }
            resolve()
          },
          // Failure
          () => {
            reject()
          })
      })
    },

    incrementNormalCorporationsCount ({ dispatch, commit, getters }: { dispatch: (actionName: string, corporation?: Corporation) => void, commit: (mutationType:string, count: number) => void, getters: { [key: string]: number }}, corporation: Corporation) {
      const count = getters.normalCorporationsCount
      commit(MutationTypes.ASSIGN_NORMAL_CORPORATIONS_COUNT, count + 1)

      for (const child of corporation.children as Array<Corporation>) {
        dispatch("incrementNormalCorporationsCount", child)
      }
    },

    resetNormalCorporationsCount({ commit }: { commit: (mutationType:string, count: number) => void }) {
      commit(MutationTypes.ASSIGN_NORMAL_CORPORATIONS_COUNT, 0)
    },

    resetCorporationsState ({ commit }: { commit: (mutationType:string) => void }) {
      commit(MutationTypes.RESET_CORPORATIONS_STATE)
    },
  },

  mutations: {
    [MutationTypes.ASSIGN_CORPORATIONS_IN_CHARGE](state: State, corporationsInCharge: Array<Corporation>) {
      state.corporationsInCharge = corporationsInCharge
    },
    [MutationTypes.ASSIGN_CORPORATION](state: State, corporation: Corporation) {
      state.corporation = corporation
    },
    [MutationTypes.ASSIGN_MONTHLY_MAIL_RECIPIENTS](state: State, monthlyMailRecipients: Array<MailRecipient>) {
      state.monthlyMailRecipients = monthlyMailRecipients
    },
    [MutationTypes.RESET_CORPORATIONS_STATE](state: State) {
      Object.assign(state, getDefaultState())
    },
    [MutationTypes.ASSIGN_CORPORATIONS_NESTED_TREE](state: State, corporationsNestedTree: Array<Corporation>) {
      state.corporationsNestedTree = corporationsNestedTree
    },
    [MutationTypes.ASSIGN_NORMAL_CORPORATIONS_COUNT](state: State, count: number) {
      state.normalCorporationsCount = count
    },
  }
}

export default corporations
